import { cache, withCache } from '@sravni/ugc/lib/utils/cache';

import { SIX_HOUR_IN_SECONDS, TWELVE_HOURS_IN_SECONDS } from '../../constants/common';
import { IOffers } from '../../interfaces/offers';
import { OrganizationType } from '../../interfaces/organizations';
import DepositsClient from '../../services/clients/DepositsClient';
import EducationClient from '../../services/clients/EducationClient';
import MortgageClient from '../../services/clients/MortgageClient';
import VitrinsClient from '../../services/clients/VitrinsClient';

interface FetchOrgProductsParams {
  productName: string;
  limit?: number;
  offset?: number;
  sortProperty?: string;
  sortDirection?: string;
  organizations?: string[];
  status?: string;
}

export interface IFetchProductsPublicParams {
  limit?: number;
  offset?: number;
  sortProperty?: string;
  sortDirection?: string;
  advertisingOnly?: boolean;
  isMix?: boolean;
  organizations?: string[];
  organizationsExclude?: string[];
  location?: string; // TODO: глянуть в сваггере, но в проекте не используем https://sravni-corp.slack.com/archives/C045KSSBV0T/p1742899512760659
  status?: string;
  fields?: string[];
  advertisingChannel?: string[];
  idsExclude?: string[];
  query?: string;
  learningtype?: string;
}

export interface IProductsList {
  items: [];
  organizations?: [];
  totalCount: number;
  totalCountGroup?: number;
}

type GetProductQuery = {
  tag: string;
  productId: string;
  organizationId: string;
  type: OrganizationType;
  vitrinsProductName: string;
  shortCard?: boolean;
};

export interface IProduct {
  id: string;
  name: string;
  type: string;
  productLogo?: string;
}

const productsByOrganizationAndTagCacheKey = (id: string, tag: string) => `products-${id}-${tag}`;

const decorateSingleProduct = (item, tag): IProduct => {
  if (!item) return null;
  return {
    id: item.id || item._id,
    name: item.name,
    productLogo: item.productLogo,
    type: tag,
  };
};

export const fetchVitrinsProducts = async (params?: FetchOrgProductsParams) => {
  try {
    const { data } = await VitrinsClient.get<IProductsList>('/v1/public/products', { params });

    return data;
  } catch (e) {}
};

export const fetchEducationProducts = async (params: IFetchProductsPublicParams) => {
  try {
    const { data } = await EducationClient.post<IProductsList>('/v1/public/products', params);
    return data;
  } catch (error) {
    // console.error('Failed to fetch education products:', error);
  }
};

export const fetchEducationProductsCached = withCache(fetchEducationProducts, { ttl: SIX_HOUR_IN_SECONDS });

export const fetchDepositProducts = async (params) => {
  const { data } = await DepositsClient.get<IProductsList>('/v1/public/products', { params });

  return data;
};

export const fetchMortgageProducts = async (params) => {
  const { data } = await MortgageClient.post<IProductsList>('/v1/public/products', params, { params });

  return data;
};

interface IVitrinsTagsConfig {
  items: {
    eventCategory: string;
    name: string;
    seoParams: {
      productName: string;
    };
  }[];
}

export type TVitrinsConfigByProductName = Record<string, { productName: string; name: string }>;

export const fetchVitrinsTagsConfig: () => Promise<TVitrinsConfigByProductName> = withCache(
  async () => {
    try {
      const { data } = await VitrinsClient.get<IVitrinsTagsConfig>('/v1/configs', {
        params: {
          fields: ['name', 'eventCategory', 'seoParams.productName'],
        },
      });

      return data.items?.reduce((acc, item) => {
        if (!item.seoParams?.productName) return acc;
        return {
          ...acc,
          [item.seoParams.productName]: {
            productName: item.name,
            name: item.eventCategory,
          },
        };
      }, {});
    } catch (e) {}
  },
  { ttl: TWELVE_HOURS_IN_SECONDS },
);

export const fetchSingleProduct = async (params: GetProductQuery) => {
  const { productId, organizationId, tag, type, vitrinsProductName, shortCard } = params;

  let item;

  let products = cache.get<IOffers['items']>(productsByOrganizationAndTagCacheKey(organizationId, tag));

  if (!products) {
    let res;

    if (type === OrganizationType.SCHOOL) {
      res = await fetchEducationProducts({
        organizations: [organizationId],
        learningtype: tag,
      });
    } else if (tag === 'savings') {
      res = await fetchDepositProducts({
        organizations: [organizationId],
      });
    } else if (tag === 'mortgage') {
      res = await fetchMortgageProducts({
        productName: 'ipoteka',
        organizations: [organizationId],
      });
    } else {
      res = await fetchVitrinsProducts({
        productName: vitrinsProductName,
        organizations: [organizationId],
      });
    }

    if (res?.items?.length) {
      cache.set(productsByOrganizationAndTagCacheKey(organizationId, tag), res.items, TWELVE_HOURS_IN_SECONDS);
      products = cache.get<IOffers['items']>(productsByOrganizationAndTagCacheKey(organizationId, tag));
    }
  }

  item = products?.find(({ id }) => id === productId);

  return shortCard ? decorateSingleProduct(item, tag) : item;
};
